import { router, useLocalSearchParams } from 'expo-router';
import { ListIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CheckInStation } from '@/components/events/check-in-station';
import { EventAttendees } from '@/components/events/event-attendees';
import { EventDetailSheet } from '@/components/events/event-detail-sheet';
import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { checkInStationMessage } from '@sixam/shared/event-checkin-core';
import { getCurrentTime } from '@/lib/dev-clock';
import { fetchEventById, type Event } from '@/lib/events';

// Long enough to read a sentence at arm's length, then out of the way.
const GREETING_VISIBLE_MS = 6000;

/**
 * The station an operator runs the start line from: the camera is the screen.
 * Nothing covers the viewfinder — the event name sits in a thin bar above it,
 * the result of each scan flashes over the bottom, and attendees are one tap
 * away in a dismissible drawer without making the operator leave the camera.
 */
export default function CheckInStationScreen() {
  const { auto, id } = useLocalSearchParams<{ auto?: string; id: string }>();
  const insets = useSafeAreaInsets();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [rosterKey, setRosterKey] = React.useState(0);
  const [isAttendeesPresented, setIsAttendeesPresented] = React.useState(false);
  // Only when the app opened itself here. Someone who tapped their way in knows
  // where they are and does not need telling.
  const [greeting, setGreeting] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    let greetingTimer: ReturnType<typeof setTimeout> | undefined;

    fetchEventById(id)
      .then((loaded) => {
        if (!active) {
          return;
        }
        setEvent(loaded);
        if (loaded && auto === '1') {
          setGreeting(checkInStationMessage(loaded, getCurrentTime()));
          greetingTimer = setTimeout(() => setGreeting(null), GREETING_VISIBLE_MS);
        }
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load event.');
        }
      });

    return () => {
      active = false;
      if (greetingTimer) {
        clearTimeout(greetingTimer);
      }
    };
  }, [auto, id]);

  function close() {
    router.replace({
      pathname: '/app/events/[id]',
      params: { id },
    });
  }

  return (
    <View className="flex-1" style={{ backgroundColor: BRAND.color.ink }}>
      <View className="flex-row items-center gap-3 px-4 py-3" style={{ zIndex: 1 }}>
        <View className="min-w-0 flex-1">
          <Text className="text-white/70" variant="label">
            Checking in to
          </Text>
          <Text className="text-lg font-bold text-white" numberOfLines={1}>
            {event?.title ?? (error ? 'Event unavailable' : 'Loading…')}
          </Text>
        </View>
        <Pressable
          accessibilityLabel="Close check-in"
          accessibilityRole="button"
          className="size-11 items-center justify-center rounded-full bg-white/10 active:opacity-70"
          hitSlop={8}
          onPress={close}
        >
          <Icon as={XIcon} className="size-6 text-white" />
        </Pressable>
      </View>

      {greeting ? (
        <Pressable
          accessibilityLabel="Dismiss"
          accessibilityRole="button"
          className="mx-4 mb-1 rounded-md px-4 py-3 active:opacity-80"
          onPress={() => setGreeting(null)}
          style={{ backgroundColor: BRAND.color.orange }}
        >
          <Text className="font-bold text-white">{greeting}</Text>
        </Pressable>
      ) : null}

      {error ? (
        <View className="flex-1 items-center justify-center p-6">
          <Text className="text-center text-white">{error}</Text>
        </View>
      ) : (
        <CheckInStation eventId={id} onCheckedIn={() => setRosterKey((key) => key + 1)} />
      )}

      <View className="gap-2 px-4 pt-3" style={{ paddingBottom: insets.bottom + 12, zIndex: 1 }}>
        <Button onPress={() => setIsAttendeesPresented(true)}>
          <Icon as={ListIcon} className="size-5 text-primary-foreground" />
          <Text>Show attendees</Text>
        </Button>
        {!event && !error ? <ActivityIndicator color="white" /> : null}
      </View>

      <EventDetailSheet
        isPresented={isAttendeesPresented}
        onDismiss={() => setIsAttendeesPresented(false)}
        title="Attendees"
      >
        <EventAttendees eventId={id} flat refreshKey={rosterKey} />
      </EventDetailSheet>
    </View>
  );
}
